import { ApolloError, BaseMutationOptions } from "@apollo/client";
import { useRequestActionMutation } from "apollo/hooks/mutations";
import { useEnvContext } from "context/EnvContext";
import {
  APP_PROVIDER_SEARCH,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { getEncryptedMessage } from "core/model/crypto/cryptoService";
import { getUndoActionType } from "core/model/requests";
import { getApolloError, getErrorMessage } from "core/model/utils/errors";
import { getReferrer } from "core/model/utils/urls";
import { encryptForSession } from "core/seald";
import {
  AuctionRequest,
  EncryptionContext,
  QueryProgress,
  RequestActionType,
  SearchActionType,
  UndoActionType,
} from "core/types";
import { useAuctionRequestEventsContext } from "dsl/atoms/AuctionRequestEventsContext";
import { useEncryption } from "dsl/atoms/Contexts";
import { setUpdatedRequest } from "dsl/ecosystems/AuctionRequest/updatedRequest";
import { useCallback, useState } from "react";

/** @deprecated To be removed when old encrytion gets decommissioned */
export const getEncryptedMessageContext = async ({
  actionType,
  decryptedSessionKey,
  encryptionContext,
  message,
}: {
  actionType: RequestActionType | SearchActionType | UndoActionType;
  decryptedSessionKey: CryptoKey | null;
  encryptionContext: EncryptionContext | undefined;
  message?: string;
}) => {
  let encryptedMessageData:
    | {
        encryptedMessage: string;
        message_iv?: string;
        sealdEncryptedMessage?: string;
      }
    | undefined;
  if (message != null && decryptedSessionKey != null) {
    try {
      encryptedMessageData = await getEncryptedMessage(
        message,
        decryptedSessionKey,
      );
      encryptedMessageData.sealdEncryptedMessage = await encryptForSession({
        context: `message for ${actionType}`,
        message,
        sessionId: encryptionContext?.seald_id,
      });
    } catch (error) {
      console.error(`Failed to encrypt message for ${actionType} - `, error);
    }
  }
  return encryptedMessageData
    ? {
        message: encryptedMessageData.encryptedMessage,
        message_iv: encryptedMessageData?.message_iv,
        seald_message: encryptedMessageData?.sealdEncryptedMessage,
      }
    : {};
};

export const getSealdEncryptedMessageContext = async ({
  actionType,
  encryptionContext,
  message,
}: {
  actionType: RequestActionType | SearchActionType | UndoActionType;
  encryptionContext: EncryptionContext | undefined;
  message?: string;
}) => {
  if (message == null) {
    return {};
  }

  try {
    const seald_message = await encryptForSession({
      context: `message for ${actionType}`,
      message,
      sessionId: encryptionContext?.seald_id,
    });

    return { seald_message };
  } catch (error) {
    console.error(`Failed to encrypt message for ${actionType} - `, error);
  }

  return {};
};

type CallRequestActionProps = {
  context?: Record<string, any>;
  message?: string;
  onCompleted?: (args?: AuctionRequest | null) => void;
  onError?: (err: ApolloError | undefined) => void;
  auctionRequest: AuctionRequest;
};

const useGetRefetchQueries = ({
  shouldRefetchEvents = true,
}: {
  shouldRefetchEvents?: boolean;
}): BaseMutationOptions["refetchQueries"] | undefined => {
  const { eventsQuery } = useAuctionRequestEventsContext();
  const { app } = useEnvContext();

  const refetchQueries: BaseMutationOptions["refetchQueries"] =
    shouldRefetchEvents && eventsQuery ? [eventsQuery] : [];

  if (app === APP_PROVIDER_SEARCH) {
    refetchQueries.push("getProviderSearchPatients");
  }

  return refetchQueries?.length ? refetchQueries : undefined;
};

const useRequestAction = ({
  actionType,
  shouldRefetchEvents,
}: {
  actionType: RequestActionType | UndoActionType;
  shouldRefetchEvents?: boolean;
}) => {
  const { decryptedSessionKey, isSealdOnly } = useEncryption();
  const refetchQueries = useGetRefetchQueries({
    shouldRefetchEvents,
  });
  const [requestAction] = useRequestActionMutation({
    actionType,
    refetchQueries,
  });

  const [queryProgress, setQueryProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  const callAction = useCallback(
    async ({
      auctionRequest,
      context = {},
      message,
      onCompleted,
      onError,
    }: CallRequestActionProps) => {
      setQueryProgress(QUERY_PROGRESS_PENDING);
      if (!auctionRequest) {
        const error = `request passed to useRequestAction is missing for actionType ${actionType}`;
        console.error(error);
        setQueryProgress(QUERY_PROGRESS_FAILED);
        throw new Error(error);
      }

      try {
        const messageData = isSealdOnly
          ? await getSealdEncryptedMessageContext({
              actionType,
              encryptionContext: auctionRequest?.seald_encryption_context,
              message,
            })
          : await getEncryptedMessageContext({
              actionType,
              decryptedSessionKey,
              encryptionContext: auctionRequest?.seald_encryption_context,
              message,
            });

        const { data } =
          (await requestAction(
            {
              context: {
                ...context,
                ...messageData,
              },
              ref: getReferrer(),
              request_id: auctionRequest.id,
            },
            { id: auctionRequest.id },
          )) ?? {};

        const requestActionResponse = data?.auctionRequest;

        if (requestActionResponse) setUpdatedRequest(requestActionResponse.id);

        if (onCompleted) onCompleted(requestActionResponse);
        setQueryProgress(QUERY_PROGRESS_SUCCEED);

        return requestActionResponse;
      } catch (err) {
        setQueryProgress(QUERY_PROGRESS_FAILED);
        console.error(`${actionType} - `, getErrorMessage(err));
        if (onError) onError(getApolloError(err));
        return null;
      }
    },
    [
      requestAction,
      actionType,
      isSealdOnly,
      decryptedSessionKey,
      refetchQueries,
    ],
  );

  return [callAction, queryProgress] as const;
};

export const useUndoRequestAction = ({
  auctionRequest,
}: {
  auctionRequest: AuctionRequest;
}) => {
  const undoActionType = getUndoActionType({
    requestActions: auctionRequest.request_actions,
  });
  const [undoAction, queryProgress] = useRequestAction({
    actionType: undoActionType,
  });
  return [undoAction, queryProgress, undoActionType] as const;
};

export default useRequestAction;
